import { normalize } from 'normalizr';

import Resources from '@helpers/resources';
import { showToast } from '@components/toast/toast-action';

import { updateEntities, resetGenericCourseRelativeEntities } from '@containers/entities/entities-reducer';
import LocalStorage from '../../helpers/local-storage';
import PortalCommunication from "../../helpers/portal-comms";
import Schema from "../entities/schema";
import { fetchClassroomsRequest, fetchClassroomsSuccess } from "../gateways/gateways-actions";

import { getAllLearningJourneys, findSchemaByTopics } from './upf-assign-util';
import * as actionTypes from './upf-assign-action-types';

export const getAssignmentsManagementInitData = (productId) => async dispatch => {
  try {
    dispatch(fetchClassroomsRequest());
    const response =
      await Resources.getAssignmentInitData
        .get({ productId })
        .send();
    const normalizedClassrooms = normalize(response.data.classrooms, Schema.CLASSROOMS_ARRAY);
    const normalizedPublications = normalize(response.data.publicationCourses, Schema.PUBLICATION_ARRAY);

    dispatch(updateEntities(normalizedClassrooms));
    dispatch(updateEntities(normalizedPublications));

    PortalCommunication.loadingSpinnerOff();
    dispatch(fetchClassroomsSuccess());
  } catch (e) {
    dispatch({
      type: actionTypes.ERROR_DETECTED,
      payload: e
    });
    throw e;
  }
}

export const getStudentsAssignments =
  ({ assignmentType, learningJourneyIds, studentUserProfileIds, productId, courseId, courseVersion }) =>
    async dispatch => {
      try {
        dispatch({ type: actionTypes.STUDENTS_ASSIGNMENTS_STATUSES_REQUEST });

        const response = await Resources.studentassignments
          .post()
          .withBody({
            assignmentType,
            learningJourneyIds,
            studentUserProfileIds,
            productId,
            courseId,
            courseVersion
          })
          .send();

        if (response.data) {
          dispatch({
            type: actionTypes.STUDENTS_ASSIGNMENTS_STATUSES_SUCCESS,
            payload: response.data
          });
        } else {
          dispatch({
            type: actionTypes.ERROR_DETECTED,
            payload: response.data
          });
        }
      } catch (err) {
        dispatch({
          type: actionTypes.ERROR_DETECTED,
          payload: err
        });
        throw err;
      }
    };

export const updateSortStudents = students => ({
  type: actionTypes.UPDATE_SORTED_STUDENTS_LIST,
  students
});

export const getGenericCoursesV2 =
  ({ courseId, productId, courseVersion }) =>
    async dispatch => {
      const DEFAULT_VERSION = 0;
      try {
        const versionId = courseVersion || DEFAULT_VERSION;
        dispatch(resetGenericCourseRelativeEntities());
        dispatch({ type: actionTypes.GENERIC_COURSES_REQUEST });

        const response = await Resources.genericCourses.get({ courseId }).withParams({ productId, versionId }).send();

        if (response?.data) {
          const key = `${courseVersion}_${courseId}_FILTER`;
          const savedFilter = LocalStorage.getItem(key);
          const {
            data,
            data: { topics }
          } = response;

          dispatch({
            type: actionTypes.GENERIC_COURSES_SUCCESS,
            payload: data,
            courseId,
            courseVersion,
            savedFilter
          });

          const normalizedData = normalize(topics, findSchemaByTopics(topics));

          dispatch(updateEntities(normalizedData));
        } else {
          dispatch({
            type: actionTypes.SHOW_TOAST,
            isToastVisible: true,
            message: response?.message
          });
        }
      } catch (err) {
        dispatch({
          type: actionTypes.ERROR_DETECTED,
          payload: err
        });
        throw err;
      }
    };

export const updateStrand = (actionType, id, checked, searchTerm) => async dispatch => {
  dispatch({
    type: actionTypes.UPDATE_STRANDS,
    id,
    checked,
    searchTerm,
    actionType
  });
};

export const applyFilter = () => async dispatch => {
  dispatch({
    type: actionTypes.APPLY_FILTER
  });
};

export const filteredStudents = students => ({
  type: actionTypes.FILTERED_STUDENTS,
  payload: students
});

export const updateSortProps = (direction, sortValue) => ({
  type: actionTypes.UPDATE_SORT_PROPERTIES,
  direction,
  sortValue
});

export const updateSelectedCourse = (selectedCourse, courseVersion) => ({
  type: actionTypes.UPDATE_SELECTED_COURSE,
  selectedCourse: {
    id: selectedCourse.value,
    version: courseVersion
  }
});

export const displayModalInfo = key => ({
  type: actionTypes.DISPLAY_MODAL_INFORMATION,
  key
});

export const hideStandard = id => ({
  type: actionTypes.HIDE_STANDARD,
  id
});

export const updateOnCellSelection = (cellData, cellStatus) => ({
  type: actionTypes.UPDATE_STUDENT_ASSIGNMENT,
  cellData,
  cellStatus
});

export const assign = (data, selectedTopics, i18n) => async dispatch => {
  try {
    dispatch({ type: actionTypes.ASSIGN_REQUEST });
    const response = await Resources.assign.post().withBody(data).send();

    if (response.data) {
      dispatch({
        type: actionTypes.ASSIGN_SUCCESS_REQUEST,
        payload: response.data
      });
      dispatch(showToast(i18n.t`Student activity assignments have been successfully updated`, 'success'));
      dispatch(
        getStudentsAssignments({
          learningJourneyIds: getAllLearningJourneys(selectedTopics),
          studentUserProfileIds: data.filteredStudents.map(student => student.id),
          productId: data.productId,
          courseId: data.courseId,
          courseVersion: data.courseVersion
        })
      );
    } else {
      dispatch({
        type: actionTypes.ASSIGN_FAILURE_REQUEST
      });
    }
  } catch (err) {
    dispatch({
      type: actionTypes.ERROR_DETECTED,
      payload: err
    });
    throw err;
  }
};

export const strandExpandCollapse = strandId => ({
  type: actionTypes.EXPAND_COLLAPSE_REQUEST,
  strandId
});

export const setSelectedClass = selectedClass => ({
  type: actionTypes.SET_SELECTED_CLASS,
  selectedClass
});

export const setSelectedGroup = selectedGroup => ({
  type: actionTypes.SET_SELECTED_GROUP,
  selectedGroup
});

export const setSelectedPublication = selectedPublication => ({
  type: actionTypes.SET_SELECTED_PUBLICATION,
  selectedPublication
});

export const setSelectedCourseOption = selectedCourseOption => ({
  type: actionTypes.SET_SELECTED_COURSE_OPTION,
  selectedCourseOption
});

export const restrictActivities = (isRestricted, classId, groupId) => async dispatch => {
  let response = null;
  try {
    dispatch({ type: actionTypes.RESTRICT_ACCESS_REQUEST });
    response = await Resources.restrictActivities.put({ isRestricted, classId, groupId }).withBody({}).send();
    if (response.status === 200) {
      dispatch({
        type: actionTypes.RESTRICT_ACCESS_SUCCESS,
        payload: isRestricted
      });
      return response;
    }
    dispatch({ type: actionTypes.RESTRICT_ACCESS_FAILURE });
    return response;
  } catch (err) {
    dispatch({ type: actionTypes.ERROR_DETECTED, payload: err });
    throw err;
  }
};

export const clearCellSelection = () => ({
  type: actionTypes.SET_ASSIGNMENT_FILTER_TYPE,
  payload: {
    selectionType: 'clearSelection'
  }
});

export const selectAssignmentHashKey = ({ assignmentHashKey, learningJourneyId, studentId }) => ({
  type: actionTypes.SELECT_ASSIGNMENT_HASH_KEY,
  assignmentHashKey,
  selectedValues: { learningJourneyId, studentId }
});

export const selectAssignmentHashKeys = assignmentHashKeys => ({
  type: actionTypes.SELECT_ASSIGNMENT_HASH_KEYS,
  assignmentHashKeys
});

export const unselectAssignmentHashKeys = assignmentHashKeys => ({
  type: actionTypes.UNSELECT_ASSIGNMENT_HASH_KEYS,
  assignmentHashKeys
});

export const resetAssignmentHashKeys = () => ({
  type: actionTypes.RESET_SELECTED_ASSIGNMENT_HASH_KEYS
});

export const disableShowGridLoader = () => ({
  type: actionTypes.DISABLE_SHOW_GRID_LOADER
});

export const updateSortedStudentIds = (sortedStudentIds) => ({
  type: actionTypes.UPDATE_SORTED_STUDENT_IDS,
  sortedStudentIds
})


// WEBPACK FOOTER //
// ./containers/upf-assign/upf-assign-actions.js